"use client";

// FILE: src/features/nmck-calculator/components/chop-card.tsx
// VERSION: 1.0.0
// START_MODULE_CONTRACT:
// PURPOSE: Карточка ЧОП с отображением Trust Score, контактов и кнопкой замены
// SCOPE: Step 4 калькулятора НМЦК — выбор/замена ЧОП
// INPUT: ChopCandidate, onReplace callback, disabled state
// OUTPUT: React-разметка карточки ЧОП
// KEYWORDS: DOMAIN(NMCC): ChopCard; TECH(React): ClientComponent
// END_MODULE_CONTRACT

import { Building2, Mail, Phone, Replace, X } from "lucide-react";
import { Button } from "@oxpa/shared/ui/button";
import { maskPhone } from "@oxpa/shared/lib/mask-phone";
import type { ChopCandidate } from "@/app/api/calculator/chops/route";

interface ChopCardProps {
  chop: ChopCandidate;
  onReplace?: () => void;
  onRemove?: () => void;
  disabled?: boolean;
}

function trustScoreColor(score: number | null): string {
  if (score === null || score === undefined) return "text-white/40 border-white/20";
  if (score >= 80) return "text-green-400 border-green-500/40";
  if (score >= 60) return "text-green-400/70 border-green-500/20";
  if (score >= 40) return "text-orange-400 border-orange-400/30";
  return "text-white/40 border-white/20";
}

function trustScoreLabel(score: number | null): string {
  if (score === null || score === undefined) return "—";
  if (score >= 80) return "Высокий";
  if (score >= 60) return "Хороший";
  if (score >= 40) return "Средний";
  return "Низкий";
}

export function ChopCard({ chop, onReplace, onRemove, disabled }: ChopCardProps) {
  return (
    <div data-testid="chop-card" className="relative border border-white/10 bg-white/5 p-4 flex flex-col gap-3 group">
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          disabled={disabled}
          className="absolute top-2 right-2 h-6 w-6 flex items-center justify-center text-white/30 hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-30 z-10"
          title="Убрать из списка"
        >
          <X className="h-4 w-4" />
        </button>
      )}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-bold text-white truncate font-mono">
            {chop.name_short}
          </h4>
        </div>
        <div className={`shrink-0 flex items-center gap-1.5 px-2 py-1 border text-[10px] font-bold uppercase tracking-wider font-mono ${trustScoreColor(chop.trust_score)}`}>
          <Building2 className="h-3 w-3" />
          {chop.trust_score ?? "—"}
          <span className="text-white/40 font-normal normal-case tracking-normal">
            {trustScoreLabel(chop.trust_score)}
          </span>
        </div>
      </div>

      <div className="space-y-1 text-xs font-mono text-white/60">
        {chop.contact_email && (
          <div className="flex items-center gap-2">
            <Mail className="h-3 w-3 shrink-0 text-white/40" />
            <span className="truncate">{chop.contact_email}</span>
          </div>
        )}
        {chop.contact_phone && (
          <div className="flex items-center gap-2">
            <Phone className="h-3 w-3 shrink-0 text-white/40" />
            <span>{maskPhone(chop.contact_phone)}</span>
          </div>
        )}
      </div>

      {onReplace && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={onReplace}
          className="rounded-none border-white/20 text-white/60 hover:text-white hover:border-white/40 h-8 text-[10px] uppercase tracking-widest font-bold w-full mt-auto"
        >
          <Replace className="h-3 w-3 mr-1.5" />
          Заменить
        </Button>
      )}
    </div>
  );
}
